// Lessons are MDX files in this folder: src/lessons/NN-slug.mdx (contract: AUTHORING.md,
// components: README.md). Every file is picked up automatically by the globs below.
//
// - `lessonMetas`: every lesson's `meta` export, loaded eagerly (only the `meta` binding is
//   imported, so the lesson bodies stay out of the main bundle). The study flow needs them for
//   the course word list.
// - `loadLesson(id)`: the lesson body, lazily, one chunk per lesson (precached by the SW).
import type { ComponentType } from 'react';
import type { LessonHub, LessonMeta } from '@valyrio/engine/types';
import { duolingoUnitsOf, normalizeLessonHub } from '@valyrio/engine/lesson-hub';
import type { CheckFocus } from '@valyrio/engine/exercise-helpers';

/**
 * LessonMeta as written in a lesson file (sources are optional citations), with the week's hub
 * fields (engine LessonHub, docs/COURSE.md): the one source of truth for the lesson's week.
 */
export interface LessonFileMeta extends LessonMeta, LessonHub {
  sources?: string[];
  /** What the end-of-lesson check tests first, e.g. { cases: ['com'] } (AUTHORING.md). */
  checkFocus?: CheckFocus;
}

const FOCUS_KEYS = ['cases', 'numbers', 'tense', 'mood', 'voice', 'degree'] as const;

/** A lesson's checkFocus made safe (lists of strings only; unknown keys dropped). */
function normalizeFocus(x: unknown): CheckFocus | undefined {
  if (!x || typeof x !== 'object') return undefined;
  const out: Record<string, string[]> = {};
  for (const k of FOCUS_KEYS) {
    const v = strings((x as Record<string, unknown>)[k]);
    if (v.length) out[k] = v;
  }
  return Object.keys(out).length ? (out as CheckFocus) : undefined;
}

export interface LessonModule {
  default: ComponentType;
  meta?: LessonFileMeta;
}

// `?meta` makes the eager imports distinct modules from the lazy ones below (the MDX plugin
// strips the query when compiling), so Rollup keeps each lesson body in its own chunk and only
// the `meta` objects land in the main bundle.
const metaModules = import.meta.glob<unknown>('./*.mdx', { query: '?meta', import: 'meta', eager: true });
const bodyModules = import.meta.glob<LessonModule>('./*.mdx');

const strings = (x: unknown): string[] => (Array.isArray(x) ? x.filter((v): v is string => typeof v === 'string') : []);

/**
 * A lesson meta made safe to use (lesson files are hand-written): id and order are required,
 * arrays default to empty, titles fall back to each other. Returns null for unusable metas.
 */
export function normalizeMeta(raw: unknown, path = ''): LessonFileMeta | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Partial<LessonFileMeta>;
  const fromPath = /(\d+)-[^/]*\.mdx$/.exec(path);
  const id = typeof m.id === 'string' && m.id ? m.id : fromPath ? path.replace(/^.*\//, '').replace(/\.mdx$/, '') : '';
  const order = typeof m.order === 'number' && m.order > 0 ? m.order : fromPath ? Number(fromPath[1]) : NaN;
  if (!id || !Number.isFinite(order)) return null;
  const titleFi = typeof m.titleFi === 'string' && m.titleFi ? m.titleFi : typeof m.titleEn === 'string' ? m.titleEn : id;
  const titleEn = typeof m.titleEn === 'string' && m.titleEn ? m.titleEn : titleFi;
  return {
    id,
    order,
    titleFi,
    titleEn,
    ...(duolingoUnitsOf(m.duolingoUnit)[0] ? { duolingoUnit: duolingoUnitsOf(m.duolingoUnit)[0]! } : {}),
    lemmaIds: strings(m.lemmaIds),
    exampleIds: strings(m.exampleIds),
    ...(Array.isArray(m.sources) ? { sources: strings(m.sources) } : {}),
    ...(normalizeFocus(m.checkFocus) ? { checkFocus: normalizeFocus(m.checkFocus)! } : {}),
    ...normalizeLessonHub(raw),
  };
}

const pathById = new Map<string, string>();

/** Every written lesson, by order (first file wins when two share an order or id). */
export const lessonMetas: readonly LessonFileMeta[] = (() => {
  const out: LessonFileMeta[] = [];
  for (const [key, raw] of Object.entries(metaModules).sort(([a], [b]) => a.localeCompare(b))) {
    const path = key.replace(/\?.*$/, '');
    const meta = normalizeMeta(raw, path);
    if (!meta) {
      if (import.meta.env.DEV) console.warn(`[lessons] ${path}: missing or invalid \`export const meta\``);
      continue;
    }
    if (pathById.has(meta.id) || out.some((m) => m.order === meta.order)) {
      if (import.meta.env.DEV) console.warn(`[lessons] ${path}: duplicate id or order ${meta.id} / ${meta.order}`);
      continue;
    }
    pathById.set(meta.id, path);
    out.push(meta);
  }
  return out.sort((a, b) => a.order - b.order);
})();

/** The lesson body component, loaded on demand. */
export async function loadLesson(id: string): Promise<ComponentType | null> {
  const path = pathById.get(id);
  const load = path ? bodyModules[path] : undefined;
  if (!load) return null;
  const mod = await load();
  return mod.default;
}
